import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Button3D } from '@/components/ui/button-3d';
import { Badge } from '@/components/ui/badge';
import { useWork } from '@/hooks/useWork';
import { WorkTrail } from '@/components/work/WorkTrail';
import { workDocumentCount } from '@/lib/work';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  ArrowLeft,
  Pencil,
  Trash2,
  FileDown,
  Loader2,
  Phone,
  MapPin,
  ReceiptText,
  CheckCircle2,
  Ban,
  RotateCcw,
  PenLine,
  FileText,
  ClipboardList,
  DollarSign,
  ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/useCompany';
import { useFeature } from '@/hooks/useFeature';
import { useReceipt, useUpdateReceipt, useDeleteReceipt, useCreateReceipt } from '@/hooks/useReceipts';
import { receiptPdfItems, receiptPaymentLabel, receiptItemLineTotal } from '@/lib/receiptItems';
import { formatQuantityDisplay } from '@/lib/quantity';
import { DuplicateDocumentButton } from '@/components/documents/DuplicateDocumentButton';
import { buildReceiptDuplicate } from '@/lib/documentDuplication';
import { todayIsoDate } from '@/lib/financeStatus';
import { useClientContact } from '@/hooks/useClientContact';
import { SendMessageDialog } from '@/components/messages/SendMessageDialog';
import { WhatsAppIcon } from '@/components/icons/WhatsAppIcon';
import { downloadQuotePDF, type PDFQuoteData } from '@/lib/pdfGenerator';
import { ThermalPrintButton, downloadThermalPdf } from '@/components/pdf/ThermalPrintButton';
import { hexToRgb } from '@/lib/pdfTemplates/shared';
import { maskDocument } from '@/lib/documentUtils';
import { PAYMENT_METHOD_LABELS, formatBRL } from '@/lib/financeLabels';
import { numeroParaReais } from '@/lib/valorExtenso';
import type { PaymentMethod } from '@/types/database';

const formatCurrency = (v: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);

const formatDate = (d?: string | null) => {
  if (!d) return '';
  const [y, m, day] = String(d).split('T')[0].split('-');
  return day && m && y ? `${day}/${m}/${y}` : String(d);
};

export default function ReceiptDetailPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const { activeCompany } = useCompany();
  const noBrandingFooter = useFeature('no_branding_footer');

  const { receipt, isLoading } = useReceipt(id);
  // Um recibo gerado a partir de uma OS carrega SÓ `service_order_id`; a âncora
  // leva os dois elos para o hook resolver a raiz (o `quote_id` daquela OS).
  const { work } = useWork(
    receipt?.id
      ? {
          kind: 'receipt',
          id: receipt.id,
          quoteId: receipt.quote_id ?? null,
          serviceOrderId: receipt.service_order_id ?? null,
        }
      : null,
  );
  // `receipts` só guarda payer_name/document — o contato vem do cadastro.
  const clientContact = useClientContact(receipt?.client_id);
  const [sendMessageOpen, setSendMessageOpen] = useState(false);
  const updateReceipt = useUpdateReceipt();
  const deleteReceipt = useDeleteReceipt();
  const createReceipt = useCreateReceipt();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [generating, setGenerating] = useState(false);

  if (!user) {
    return (
      <MainLayout>
        <Header onBack={() => navigate('/recibos')} />
        <p className="py-16 text-center text-sm text-muted-foreground">Faça login para continuar.</p>
      </MainLayout>
    );
  }

  if (!receipt) {
    return (
      <MainLayout>
        <Header onBack={() => navigate('/recibos')} />
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <ReceiptText className="mb-4 h-12 w-12 text-muted-foreground" />
            <p className="text-muted-foreground">
              {isLoading ? 'Carregando…' : 'Recibo não encontrado.'}
            </p>
            <Button variant="outline" className="mt-4" onClick={() => navigate('/recibos')}>
              Voltar
            </Button>
          </CardContent>
        </Card>
      </MainLayout>
    );
  }

  const amount = Number(receipt.amount ?? 0);
  const cancelled = Boolean(receipt.cancelled_at);
  const isPending = receipt._offline_pending === true;
  // Fonte única com a exportação em massa: pagamento DIVIDIDO (Pix + dinheiro)
  // só aparece se as duas telas lerem a lista da mesma maneira.
  const paymentLabel = receiptPaymentLabel(receipt, PAYMENT_METHOD_LABELS);
  // MESMA fonte que o payload do PDF usa: tela e papel não têm como divergir.
  const itensDoRecibo = receiptPdfItems(receipt);

  // Origem — de onde este recibo veio (rastreabilidade). Prioriza orçamento >
  // OS > lançamento financeiro; ids offline ainda não têm destino navegável.
  const origin =
    receipt.quote_id && !String(receipt.quote_id).startsWith('offline_')
      ? { to: `/quotes/${receipt.quote_id}`, label: 'Origem: orçamento', Icon: FileText }
      : receipt.service_order_id && !String(receipt.service_order_id).startsWith('offline_')
      ? { to: `/ordens-servico/${receipt.service_order_id}`, label: 'Origem: ordem de serviço', Icon: ClipboardList }
      : receipt.finance_entry_id
      ? { to: '/financeiro', label: 'Origem: lançamento financeiro', Icon: DollarSign }
      : null;

  const handleDelete = async () => {
    try {
      await deleteReceipt.mutateAsync(receipt.id);
      toast.success('Recibo excluído.');
      navigate('/recibos');
    } catch {
      // handled by mutation
    }
  };

  const toggleCancel = async () => {
    try {
      await updateReceipt.mutateAsync({
        id: receipt.id,
        updates: { cancelled_at: cancelled ? null : new Date().toISOString() },
      });
      toast.success(cancelled ? 'Recibo reativado.' : 'Recibo cancelado.');
    } catch {
      // handled by mutation
    }
  };

  /** Fonte única do A4 e do cupom térmico deste recibo. */
  const buildReceiptPdfData = (): PDFQuoteData => {
    const company = activeCompany as any;
    return {
      documentType: 'recibo',
      client: {
        name: receipt.payer_name || 'Não informado',
        address: receipt.payer_address || undefined,
        document: receipt.payer_document || undefined,
      },
      company: company
        ? {
            name: company.name || 'Minha Empresa',
            cnpj: company.cnpj_cpf || undefined,
            address: company.address || undefined,
            phone: company.phone || undefined,
            email: company.email || undefined,
            logoUrl: company.logo_url || undefined,
          }
        : undefined,
      quoteDate: receipt.issue_date
        ? `${String(receipt.issue_date).split('T')[0]}T00:00:00`
        : new Date().toISOString(),
      // As LINHAS do recibo (lacuna L1). Vazio = recibo sem discriminacao,
      // que e o estado de todo o historico e sai no PDF como sempre saiu.
      items: receiptPdfItems(receipt),
      total: amount,
      isPremium: noBrandingFooter.allowed,
      brandColorRGB: company ? hexToRgb(company.primary_color || '#0d9488') : [13, 148, 136],
      sequenceId: receipt.sequence_id ?? undefined,
      showSequenceId: receipt.show_sequence_id ?? false,
      receiptDescription: receipt.description,
      receiptPaymentMethodLabel: paymentLabel,
      receiptIssuePlace: receipt.issue_place,
      receiptNotes: receipt.notes,
      receiptPaymentTerms: receipt.payment_terms,
      issuerSignatureDataUrl: receipt.issuer_signature,
      issuerSignerName: receipt.issuer_signer_name,
      logoWatermark: company?.logo_watermark === true,
    };
  };

  const payerSlug = (receipt.payer_name || 'pagador').replace(/[^a-zA-Z0-9]/g, '_');

  const generatePDF = async () => {
    setGenerating(true);
    try {
      await downloadQuotePDF(buildReceiptPdfData(), `recibo-${payerSlug}-${Date.now()}.pdf`);
    } catch (e) {
      console.error(e);
      toast.error('Não foi possível gerar o PDF.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <MainLayout>
      <Header
        onBack={() => navigate('/recibos')}
        action={
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="icon" onClick={() => navigate(`/recibos/${receipt.id}/editar`)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => setConfirmDelete(true)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        }
      />

      <div className="space-y-4">
        {/* Trilha do trabalho quando há corrente; senão o link simples de origem
            — que também cobre o caso que a corrente NÃO alcança: recibo nascido
            de um lançamento financeiro (`finance_entries` não tem `receipt_id`). */}
        {workDocumentCount(work) > 1 ? (
          <WorkTrail
            work={work!}
            anchorId={receipt.id}
            onOpenDocument={(route) => navigate(route)}
          />
        ) : (
          origin && (
          <button
            type="button"
            onClick={() => navigate(origin.to)}
            className="flex w-full items-center justify-between gap-2 rounded-md border border-dashed p-2.5 text-left text-sm transition hover:bg-muted"
          >
            <span className="flex items-center gap-2 text-muted-foreground">
              <origin.Icon className="h-4 w-4" />
              {origin.label}
            </span>
            <ChevronRight className="h-4 w-4 opacity-60" />
          </button>
          )
        )}

        {/* Overview */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-start justify-between gap-3">
              <CardTitle className="text-lg">{receipt.payer_name || 'Recibo'}</CardTitle>
              <div className="flex shrink-0 items-center gap-1">
                {isPending && <Badge variant="outline">Offline</Badge>}
                {receipt.show_sequence_id && receipt.sequence_id && (
                  <Badge variant="secondary" className="border-0">
                    Nº {String(receipt.sequence_id).padStart(4, '0')}
                  </Badge>
                )}
                {cancelled && (
                  <Badge className="border-0 bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400">
                    Cancelado
                  </Badge>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              {receipt.payer_document && (
                <p className="text-xs text-muted-foreground">Doc.: {maskDocument(receipt.payer_document)}</p>
              )}
              {receipt.payer_address && (
                <p className="flex items-center gap-1.5 text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" /> {receipt.payer_address}
                </p>
              )}
            </div>

            <div className="rounded-lg bg-muted/60 px-3 py-3">
              <div className="flex items-center justify-between">
                <span className="text-xs text-muted-foreground">Valor recebido</span>
                <span className="text-xl font-bold">{formatCurrency(amount)}</span>
              </div>
              {amount > 0 && (
                <p className="mt-1 text-xs capitalize text-muted-foreground">{numeroParaReais(amount)}</p>
              )}
            </div>

            {/* AS LINHAS DO RECIBO (L1).
                Achado no aparelho: o PDF imprimia a tabela e ESTA TELA mostrava
                só o total — a divergência tela × papel do #135, no mesmo lugar
                em que ela já tinha acontecido com a garantia. Quem confere o
                recibo no app precisa ver o que o cliente vê no papel.
                Sem linhas, nada é desenhado e a tela fica como sempre foi. */}
            {itensDoRecibo.length > 0 && (
              <div className="border-t pt-2">
                <p className="mb-1 text-xs font-medium text-muted-foreground">Itens</p>
                <div className="space-y-1">
                  {itensDoRecibo.map((item, index) => (
                    <div key={index} className="flex items-start justify-between gap-2 text-xs">
                      {/* `min-w-0` não é decoração (#175): sem ele um nome sem
                          espaços estica a linha para fora da viewport. */}
                      <span className="min-w-0 flex-1 break-words">
                        {formatQuantityDisplay(item.quantity)}
                        {item.unit ? ` ${item.unit}` : ''} × {item.name}
                      </span>
                      <span className="shrink-0 font-medium">
                        {formatBRL(receiptItemLineTotal(item))}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {receipt.description && (
              <div>
                <p className="text-xs font-medium text-muted-foreground">Referente a</p>
                <p className="whitespace-pre-wrap">{receipt.description}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-2 border-t pt-2 text-xs">
              {paymentLabel && (
                <div>
                  <p className="text-muted-foreground">Forma de pagamento</p>
                  <p className="font-medium">{paymentLabel}</p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground">Emissão</p>
                <p className="font-medium">
                  {formatDate(receipt.issue_date)}
                  {receipt.issue_place ? ` · ${receipt.issue_place}` : ''}
                </p>
              </div>
            </div>

            {receipt.notes && (
              <div className="border-t pt-2">
                <p className="text-xs font-medium text-muted-foreground">Observações</p>
                <p className="whitespace-pre-wrap text-xs text-muted-foreground">{receipt.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Signature state */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Assinatura do emitente</CardTitle>
          </CardHeader>
          <CardContent className="text-sm">
            {receipt.issuer_signature ? (
              <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                <CheckCircle2 className="h-4 w-4" />
                <span className="text-xs">
                  Assinado{receipt.issuer_signer_name ? ` por ${receipt.issuer_signer_name}` : ''}.
                </span>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-muted-foreground">
                <PenLine className="h-4 w-4" />
                <span className="text-xs">Sem assinatura. Edite o recibo para assinar (opcional).</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Button3D className="w-full" onClick={generatePDF} disabled={generating}>
          {generating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileDown className="mr-2 h-4 w-4" />}
          Gerar PDF
        </Button3D>

        {/* Via de balcão: entregue na hora a quem pagou. O A4 acima continua
            sendo a via de arquivo — papel térmico desbota em meses. */}
        <ThermalPrintButton
          onPrint={(format) =>
            downloadThermalPdf(buildReceiptPdfData(), `recibo-${payerSlug}`, format)
          }
        />

        {/* Recibo recorrente (aluguel, mensalidade) é o caso clássico: mesmo
            pagador, mesmo valor, mês novo. */}
        <DuplicateDocumentButton
          className="w-full"
          description="A cópia sai com a data de HOJE e um número de recibo novo. Ela não herda o cancelamento nem o vínculo com o orçamento, a ordem de serviço ou o lançamento do financeiro — esses amarram o recibo a UM pagamento, e duas vias apontando para o mesmo dariam a impressão de dois recebimentos."
          onDuplicate={async () => {
            const created = await createReceipt.mutateAsync(
              buildReceiptDuplicate(receipt as any, { todayIso: todayIsoDate() }),
            );
            toast.success('Recibo duplicado.');
            if (created.id) navigate(`/recibos/${created.id}`);
            return created.id ?? null;
          }}
        />

        {/* Envio ao pagador. Não desabilita offline: os modelos ficam em cache e
            compor a mensagem é local. */}
        <Button3D color="whatsapp" className="w-full" onClick={() => setSendMessageOpen(true)}>
          <WhatsAppIcon className="mr-2 h-4 w-4" />
          Enviar por WhatsApp
        </Button3D>

        <Button
          variant="ghost"
          className={`w-full ${cancelled ? '' : 'text-destructive'}`}
          onClick={() => (cancelled ? toggleCancel() : setConfirmCancel(true))}
        >
          {cancelled ? (
            <>
              <RotateCcw className="mr-2 h-4 w-4" /> Reativar recibo
            </>
          ) : (
            <>
              <Ban className="mr-2 h-4 w-4" /> Cancelar recibo
            </>
          )}
        </Button>
      </div>

      {/* Edit modal */}

      {/* Cancel confirm */}
      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar recibo?</AlertDialogTitle>
            <AlertDialogDescription>
              O recibo ficará marcado como cancelado. Você pode reativá-lo depois.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={toggleCancel}>Cancelar recibo</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirm */}
      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir recibo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. O recibo será removido.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Envio do recibo ao pagador (modelo + variáveis + revisão) */}
      <SendMessageDialog
        open={sendMessageOpen}
        onOpenChange={setSendMessageOpen}
        category="receipt"
        context={{
          cliente: receipt.payer_name ?? '',
          numero: receipt.sequence_id ?? '',
          valor: amount,
          data: receipt.issue_date ?? null,
          empresa: activeCompany?.name ?? '',
        }}
        phone={clientContact.phone}
        title="Enviar recibo"
      />
    </MainLayout>
  );
}

function Header({ onBack, action }: { onBack: () => void; action?: React.ReactNode }) {
  return (
    <div className="mb-2 flex items-center gap-3">
      <Button variant="ghost" size="icon" onClick={onBack}>
        <ArrowLeft className="h-5 w-5" />
      </Button>
      <h1 className="flex-1 text-xl font-semibold">Recibo</h1>
      {action}
    </div>
  );
}
